/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { DialogComponent } from '@theme/dialog';
import klaviyo from '@theme/klaviyo';

/**
 * Back-in-stock notify-me dialog. Rendered once per page via
 * `snippets/klaviyo-bis-modal.liquid`; every <notify-me-component> finds
 * this singleton and opens it via `openForVariant()`.
 */
export class KlaviyoBisDialog extends DialogComponent {
  /** @type {string|null} */
  #variantId = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form?.addEventListener('submit', this.#handleSubmit);
  }

  /**
   * Show the dialog primed for a specific variant.
   * @param {{ variantId: string|number, productTitle?: string, variantTitle?: string }} args
   */
  openForVariant({ variantId, productTitle, variantTitle }) {
    this.#variantId = String(variantId);
    if (this.refs.productHeading) this.refs.productHeading.textContent = productTitle || '';
    if (this.refs.variantSubtitle) this.refs.variantSubtitle.textContent = variantTitle || '';
    this.#setStatus('');
    this.showDialog();
    requestAnimationFrame(() => this.refs.emailInput?.focus());
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.#variantId) return;

    const email = this.refs.emailInput?.value.trim() ?? '';
    const optin = Boolean(this.refs.optinCheckbox?.checked);

    this.#setBusy(true);
    this.#setStatus('');

    try {
      await klaviyo.createClientBackInStockSubscription({ email, variantId: this.#variantId });
      if (optin) {
        // Newsletter opt-in is best-effort — don't fail the BIS flow on it.
        try {
          await klaviyo.createClientSubscription({ email });
        } catch {
          /* swallow */
        }
      }
      this.#setStatus(this.dataset.successText || 'You are on the list!', 'success');
      this.refs.form?.reset();
    } catch (err) {
      this.#setStatus(err?.message || this.dataset.errorText || 'Subscription failed.', 'error');
    } finally {
      this.#setBusy(false);
    }
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.toggleAttribute('busy', busy);
    if (this.refs.submitButton) this.refs.submitButton.disabled = busy;
  }

  /** @param {string} text @param {'success'|'error'|''} [variant] */
  #setStatus(text, variant = '') {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = text;
    status.dataset.variant = variant;
  }
}

if (!customElements.get('klaviyo-bis-dialog')) {
  customElements.define('klaviyo-bis-dialog', KlaviyoBisDialog);
}
